import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import * as VesselUtil from "@/utils/VesselUtil";
import * as RGUtil from "@/utils/RadarGridObjectUtil";
import * as VHUtil from "@/utils/VoyageHistoryUtil";
import C from "@/views/enterprise/rg-object-history/Constants";
import * as turf from "@turf/turf";
import moment from "moment-timezone";
import FireStoreService from "@/services/fireStoreService";
import isDate from "lodash/isDate";
import chunk from "lodash/chunk";
import isNumber from "lodash/isNumber";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import isUndefined from "lodash/isUndefined";
import Vue from "vue";
import { db } from "@/firebase/firebaseConfig";

export default {
  async getAisVesselsHistory({ commit }, payload) {
    let resMyVessels = await AiseaApiFactory.get("enterpriseVessel").index();

    let params = {
      started_at: payload.startTime,
      ended_at: payload.endTime,
      north_east_latitude: payload.coordinates[3][1],
      north_east_longitude: payload.coordinates[3][0],
      south_west_latitude: payload.coordinates[1][1],
      south_west_longitude: payload.coordinates[1][0],
    };
    // AIS船
    let resAisVessels = await AiseaApiFactory.get(
      "enterpriseVoyageRecords",
    ).searchVesselsPoints(params);

    let resPlayBackObjList = [];
    for (const value of Object.values(resAisVessels.data)) {
      let vessel = VHUtil.vessel(value);
      let found = resMyVessels.data.vessels.find(
        (el) => el.id === vessel.vesselId,
      );
      let isMyVessel = typeof found !== "undefined";
      resPlayBackObjList.push({
        ...vessel,
        heading: vessel.pointsArray[0].heading,
        startTime: vessel.pointsArray[0].epoch,
        endTime: vessel.pointsArray[vessel.pointsArray.length - 1].epoch,
        objectType: isMyVessel
          ? C.PLAYBACK_OBJECT_TYPE.MY_VESSELS
          : C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS,
      });
    }

    //不審船(RGObject)
    let resRgObjects = await AiseaApiFactory.get("radarGridObjects").search(
      params,
    );
    for (const objectId of Object.keys(resRgObjects.data)) {
      const rgObject = resRgObjects.data[objectId];
      resPlayBackObjList.push({
        objectId,
        trackingNumber: rgObject.n,
        heading: rgObject.points[0].cog,
        pointsArray: rgObject.points,
        startTime: rgObject.points[0].t,
        endTime: rgObject.points[rgObject.points.length - 1].t,
        objectType: C.PLAYBACK_OBJECT_TYPE.RG_OBJECT,
      });
    }

    commit("SET_RG_PLAYBACK_OBJECTS", resPlayBackObjList);

    //commit("SET_BUFFERED_PERCENT", 100);
  },

  async generateCurrentGeoJson({ state, getters }) {
    if (!Array.isArray(state.rgPlaybackObjects)) {
      return {
        points: turf.featureCollection([]),
        tracks: turf.featureCollection([]),
      };
    }

    const { rgPlaybackObjects, playbackSettings } = state;
    const currentPlaybackData = rgPlaybackObjects
      .filter(({ startTime }) => {
        if (!isNumber(startTime)) return false;
        return startTime <= playbackSettings.now; // 開始時刻が現在時刻より後のものは表示しない。
      })
      .filter(({ endTime }) => {
        if (!isNumber(endTime)) return false;
        const dateFrom = moment.unix(playbackSettings.now);
        const dateTo = moment.unix(endTime);
        const diff = dateFrom.diff(dateTo, "minutes");
        return diff < 1; // 1分経過したものは表示しない。
      })
      .map((rgPlayBackObj) => {
        const { pointsArray, objectType } = rgPlayBackObj;
        const idx = calcClosestPrevPointIndex(
          pointsArray,
          playbackSettings.now,
        );
        const prevPoint = pointsArray[idx];
        const nextPoint = pointsArray[idx + 1];

        const interpolateRate =
          objectType === C.PLAYBACK_OBJECT_TYPE.RG_OBJECT
            ? 0 // rgObjectは補間しない(補間率が0)
            : calcInterpolateRate({
                prevPoint,
                currentTimeStamp: playbackSettings.now,
                nextPoint,
              });
        const currentPoint = calcCurrentPoint(
          prevPoint,
          nextPoint,
          interpolateRate,
        );
        return { rgPlayBackObj, idx, currentPoint };
      });

    // 戻りをまとめる
    return {
      points: turf.featureCollection(
        currentPlaybackData.reduce(
          (pointsFeatures, { rgPlayBackObj, currentPoint }) => {
            pointsFeatures.push(
              ...createPointsFeatures(rgPlayBackObj, currentPoint),
            );
            return pointsFeatures;
          },
          [],
        ),
      ),
      tracks: turf.featureCollection(
        currentPlaybackData.reduce(
          (trackFeatures, { rgPlayBackObj, idx, currentPoint }) => {
            const { objectType } = rgPlayBackObj;
            trackFeatures.push(
              ...createTrackFeatures(
                rgPlayBackObj,
                currentPoint,
                idx,
                getters.isVisibleTrack(
                  objectType,
                  getRgPlaybackObjectId(rgPlayBackObj),
                ),
              ),
            );
            return trackFeatures;
          },
          [],
        ),
      ),
    };
  },

  async setRgPlaybackPrivatePlots(
    { commit, rootGetters },
    {
      historyStartDate, // Date
      historyEndDate, // Date
    },
  ) {
    if (!rootGetters.getAddonFlag("GKMG_PRIVATE_PLOT")) {
      throw new Error("Error Invalid Privilege");
    }
    if (!isDate(historyStartDate) || !isDate(historyEndDate)) {
      throw new Error("Error Invalid Augments");
    }
    const privatePlotsForPlayBack = (
      await FireStoreService.getPrivatePlotsCreatedAtTheTime(historyEndDate)
    ) // historyEndDate までに作成された privatePlot を取得
      .filter((plot) => plot)
      .filter(
        ({ deleted_at: deletedAt }) =>
          !(deletedAt && deletedAt.toDate() < historyStartDate), // 履歴再生開始時に削除済のものを省く
      )
      .filter(({ start_date: startDate, end_date: endDate }) => {
        if (!startDate && !endDate) return true; // 公開期間がないものは表示
        if (endDate && endDate.toDate() < historyStartDate) return false; // 履歴再生開始時に公開終了しているものを省く
        if (startDate && historyEndDate < startDate.toDate()) return false; // 履歴再生終了後に公開開始になるものを省く
        return true;
      });

    commit("SET_RG_PLAYBACK_PRIVATE_PLOTS", privatePlotsForPlayBack);
  },

  async fetchCameras() {
    const response = await AiseaApiFactory.get("enterpriseCamera").search();
    if (response && response.data && Array.isArray(response.data.cameras)) {
      return response.data.cameras;
    }
    return [];
  },

  async setCameras({ commit, dispatch }) {
    try {
      const cameras = await dispatch("fetchCameras");
      commit("SET_RG_PLAYBACK_CAMERAS", cameras);
    } catch (error) {
      Vue.$log.debug(error);
    }
  },

  async getRadarCameraMastersByHashList(
    _,
    {
      cameraHashList, // Array<String>
    },
  ) {
    const radarCameraMasters = {};
    const collectionRef = db.collection(
      C.RG_FB_COLLECTIONS.RADAR_CAMERA_MASTER,
    );
    await Promise.allSettled(
      chunk(cameraHashList, 5).map(async (chunked) => {
        const chunkedCameraMasters = await collectionRef
          .where("a", "in", chunked)
          .get();
        chunkedCameraMasters.forEach((querySnapshot) => {
          radarCameraMasters[querySnapshot.id] = querySnapshot.data();
        });
      }),
    );
    return radarCameraMasters;
  },

  async setRadarCameraMasters({ commit, dispatch, getters }) {
    try {
      const radarCameraMasters = await dispatch(
        "getRadarCameraMastersByHashList",
        { cameraHashList: Object.keys(getters.cameraHashMap) },
      );
      commit("SET_RADAR_CAMERA_MASTERS", radarCameraMasters);
    } catch (error) {
      Vue.$log.debug(error);
    }
  },

  async setRadarCameraData({ dispatch }) {
    await dispatch("setCameras");
    await dispatch("setRadarCameraMasters");
  },

  // 指定したRgObjectを選択状態にする
  async addVisibleTracksRgObject(
    { commit, getters },
    {
      toBeAddedId, // string(RgObjectのid)
    },
  ) {
    const visibleTrackRgObjectSet = new Set(getters.visibleTrackRgObjects);
    if (!visibleTrackRgObjectSet.has(toBeAddedId)) {
      const nextVisibleTrackRgObjects = [
        ...visibleTrackRgObjectSet,
        toBeAddedId,
      ];
      commit("SET_VISIBLE_TRACKS_RG_OBJECT", nextVisibleTrackRgObjects);
    }
  },

  // 指定したRgObjectの選択状態を解除
  async removeVisibleTracksRgObject(
    { commit, getters },
    {
      toBeRemovedId, // string(RgObjectのid)
    },
  ) {
    const nextVisibleTrackRgObjects = getters.visibleTrackRgObjects.filter(
      (objectId) => objectId !== toBeRemovedId,
    );
    commit("SET_VISIBLE_TRACKS_RG_OBJECT", nextVisibleTrackRgObjects);
  },

  // RgObjectの選択状態を全て解除
  async resetVisibleTracksRgObject({ commit }) {
    commit("SET_VISIBLE_TRACKS_RG_OBJECT", []);
  },

  async resetVisibleTracks({ commit }) {
    commit("RESET_VISIBLE_TRACKS");
  },

  async filterAndAddExpandImgRgObjectById(
    { dispatch },
    {
      rgObject, //  {id: string, src: string, trackNumber: string, aiseaCameraName: string}]
    },
  ) {
    const expandImgObject = RGUtil.createExpandImgObject(rgObject);
    const {
      hasValidId,
      hasValidTrackNumber,
      hasValidAiseaCameraName,
      hasValidUrl,
      value,
    } = expandImgObject;
    if (!hasValidId || !hasValidTrackNumber || !hasValidUrl) {
      Vue.$log.debug("Error Invalid expandImgObject:", expandImgObject);
      throw new Error("Error Invalid expandImgObject", expandImgObject);
    } else if (!hasValidAiseaCameraName) {
      Vue.$log.debug("Error Invalid expandImgObject:", expandImgObject);
    }

    await dispatch("filterExpandImgRgObject", value); // 重複を削除
    await dispatch("pushExpandImgRgObject", { rgObject: value }); // 追加
  },

  // 拡大表示する対象を追加
  async pushExpandImgRgObject(
    { commit },
    {
      rgObject, //  {id: string, src: string, trackNumber: string, aiseaCameraName: string}]
    },
  ) {
    const expandImgObject = RGUtil.createExpandImgObject(rgObject);
    const {
      hasValidId,
      hasValidTrackNumber,
      hasValidAiseaCameraName,
      hasValidUrl,
      value,
    } = expandImgObject;
    if (!hasValidId || !hasValidTrackNumber || !hasValidUrl) {
      Vue.$log.debug("Error Invalid expandImgObject:", expandImgObject);
      throw new Error("Error Invalid expandImgObject", expandImgObject);
    } else if (!hasValidAiseaCameraName) {
      Vue.$log.debug("Error Invalid expandImgObject:", expandImgObject);
    }
    commit("PUSH_EXPAND_IMG_RG_OBJECT", value);
  },

  async filterExpandImgRgObject(
    { commit },
    {
      id, // string
    },
  ) {
    commit("FILTER_EXPAND_IMG_RG_OBJECT", id);
  },

  // 拡大表示の対象を全て解除
  async resetExpandImgRgObject({ commit }) {
    commit("SET_EXPAND_IMG_RG_OBJECT", []);
  },

  async resetState({ commit }) {
    commit("RESET_STATE");
  },
};

/**
 * 指定されたタイムスタンプに最も近い要素を二分探索で検索します。
 * O(log n)
 *
 * @param {Array} pointsArray - ポイントの配列 (rgPlaybackObjects.pointsArray)
 * @param {number} timestamp - タイムスタンプ (playbackSettings.now)
 * @returns {number} - 最も近い要素のインデックス
 */
const binarySearchClosest = (
  pointsArray, // :Array (rgPlaybackObjects.pointsArray)
  timestamp, // :number playbackSettings.now
) => {
  let left = 0;
  let right = pointsArray.length - 1;
  let closest = -1;

  while (left <= right) {
    const midIndex = Math.floor((left + right) / 2); // 中間のindex
    const midObject = pointsArray[midIndex];
    const midTimestamp = getPointTimestamp(midObject);

    if (timestamp < midTimestamp) {
      right = midIndex - 1;
    } else if (timestamp > midTimestamp) {
      left = midIndex + 1;
    } else {
      return midIndex;
    }
    closest = midIndex;
  }
  return closest;
};

/**
 * 指定された時間に最も近いポイントのインデックスを取得します。
 * 未通過のポイントが最も近い場合は、その前のポイントのインデックスを返します。
 * 最も近いポイントがなかった場合は、-1を返します。
 * O(log n)
 *
 * @param {Array} pointsArray - ポイントの配列 (rgPlaybackObjects.pointsArray)
 * @param {number} timeToDisplay - 表示する時間 (playbackSettings.now)
 * @returns {number} - 最も近いポイントのインデックス
 */
const calcClosestPrevPointIndex = (pointsArray, timeToDisplay) => {
  const closestIndex = binarySearchClosest(pointsArray, timeToDisplay); // O(log n)

  if (closestIndex === -1) return closestIndex;
  if (timeToDisplay < getPointTimestamp(pointsArray[closestIndex])) {
    return closestIndex - 1;
  }
  return closestIndex;
};

/**
 * rgPlayBackObj、interpolatedCurrentPoint、currentIndexを使用してトラックのフィーチャーを作成します。
 *
 * @param {Object} rgPlayBackObj
 * @param {Object} interpolatedCurrentPoint - 線形補間された現在のポイント
 * @param {number} currentIndex - 現在のインデックス
 * @param {boolean} [isVisibleTrack=false] - トラックの可視性（省略可）
 * @returns {Object} トラックのフィーチャー(Array<turf.lineString>)
 */
const createTrackFeatures = (
  rgPlayBackObj,
  interpolatedCurrentPoint,
  currentIndex,
  isVisibleTrack = false, // boolean
) => {
  if (!isObject(rgPlayBackObj)) return [];
  if (!isObject(interpolatedCurrentPoint)) return [];
  if (!isNumber(currentIndex)) return [];

  const coordinates = [
    interpolatedCurrentPoint.lon,
    interpolatedCurrentPoint.lat,
  ];
  const trackFeaturesCreator =
    rgPlayBackObj.objectType === C.PLAYBACK_OBJECT_TYPE.RG_OBJECT
      ? RGUtil.createTrackFeatures
      : VHUtil.createTrackFeatures;
  const trackFeature = trackFeaturesCreator(
    rgPlayBackObj.objectType,
    getRgPlaybackObjectId(rgPlayBackObj),
    isVisibleTrack,
    rgPlayBackObj.pointsArray,
    currentIndex + 1,
    coordinates,
    rgPlayBackObj,
  );
  return trackFeature;
};

/**
 * ポイントのフィーチャーの配列を作成します。
 * ポイントのフィーチャーには、ポイントとポリゴンが含まれます。
 *
 * @param {Object} rgPlayBackObj
 * @param {Object} interpolatedCurrentPoint - 補間された現在のポイント
 * @returns {Array} ポイントのフィーチャーの配列(Array<turf.feature>)
 */
const createPointsFeatures = (rgPlayBackObj, interpolatedCurrentPoint) => {
  if (!isObject(rgPlayBackObj)) return [];
  if (!isObject(interpolatedCurrentPoint)) return [];

  const { MY_VESSELS, OTHER_VESSELS, RG_OBJECT } = C.PLAYBACK_OBJECT_TYPE;
  const { objectType } = rgPlayBackObj;
  if (![MY_VESSELS, OTHER_VESSELS, RG_OBJECT].includes(objectType)) return [];

  if (objectType === MY_VESSELS || objectType === OTHER_VESSELS) {
    return [
      createVesselPointFeature(rgPlayBackObj, interpolatedCurrentPoint),
      ...createVesselPolygonFeatures(rgPlayBackObj, interpolatedCurrentPoint),
    ];
  } else if (
    // 不審船(RGObject)
    objectType === RG_OBJECT
  ) {
    const { cog, k, ai_r, man_r, sog, lat, lon, p, t } =
      interpolatedCurrentPoint;
    const props = RGUtil.createRgObjectProperty({
      ...rgPlayBackObj,
      cog,
      k,
      ai_r,
      man_r,
      sog,
      p,
      t,
      // lat, // TODO: 将来的にはlat, lonを使用する予定
      // lon,
    });

    return [
      turf.feature(
        {
          type: "Point",
          coordinates: [lon, lat], // 不審船(RGObject)は位置を補完しない
        },
        props,
        { id: rgPlayBackObj.objectId },
      ),
    ];
  }
  return [];
};

/**
 * rgPlayBackObjと補間された現在のポイントを使用して、船舶のポイントフィーチャを作成します。
 *
 * @param {Object} rgPlayBackObj
 * @param {Object} interpolatedCurrentPoint - 補間された現在のポイント
 * @returns {Object} - 船舶のポイントフィーチャ(turf.feature)
 */
const createVesselPointFeature = (rgPlayBackObj, interpolatedCurrentPoint) => {
  if (!isObject(rgPlayBackObj)) return [];
  if (!isObject(interpolatedCurrentPoint)) return [];

  const { MY_VESSELS, OTHER_VESSELS } = C.PLAYBACK_OBJECT_TYPE;
  const { objectType } = rgPlayBackObj;
  if (![MY_VESSELS, OTHER_VESSELS].includes(objectType)) return [];
  const { lon, lat, heading } = interpolatedCurrentPoint;
  const props = {
    vesselId: rgPlayBackObj.vesselId,
    vesselName: rgPlayBackObj.vesselName,
    objectType: rgPlayBackObj.objectType,
    epoch: rgPlayBackObj.epoch,
    hasSize: rgPlayBackObj.hasSize,
    icon: VesselUtil.getSymbolIconNameLightWeight(
      Number(rgPlayBackObj.vesselType),
      1.0,
      0,
    ),
    heading: heading || 0,
    marker: "vessel-owned-marker-25",
  };

  return turf.feature(
    {
      type: "Point",
      coordinates: [lon, lat],
    },
    props,
  );
};

/**
 * rgPlayBackObjと補間された現在のポイントを受け取り、
 * 船舶のポリゴンフィーチャーを作成します。
 *
 * @param {Object} rgPlayBackObj - rgPlayBackObj
 * @param {Object} interpolatedCurrentPoint - 補間された現在のポイント
 * @returns {Array} 船舶のポリゴンフィーチャーの配列
 */
const createVesselPolygonFeatures = (
  rgPlayBackObj,
  interpolatedCurrentPoint,
) => {
  if (!isObject(rgPlayBackObj)) return [];
  if (!isObject(interpolatedCurrentPoint)) return [];
  if (!rgPlayBackObj.hasSize) return [];

  const { MY_VESSELS, OTHER_VESSELS } = C.PLAYBACK_OBJECT_TYPE;
  const { objectType } = rgPlayBackObj;
  if (![MY_VESSELS, OTHER_VESSELS].includes(objectType)) return [];

  const { lon, lat, heading } = interpolatedCurrentPoint;
  return VHUtil.createPolygonFeature(
    rgPlayBackObj.vesselId,
    {
      lon,
      lat,
      h: heading,
    },
    rgPlayBackObj.polygonData,
  ).map((element) => {
    element.properties["vesselId"] = rgPlayBackObj.vesselId;
    element.properties["hasSize"] = rgPlayBackObj.hasSize;
    element.properties["objectType"] = objectType;
    return element;
  });
};

/**
 * 現在のタイムスタンプに基づいて、2つのポイント間の補間率を計算します。
 * @param {Object} options - オプションオブジェクト。
 * @param {Object} options.prevPoint - 前のポイントオブジェクト。
 * @param {Object} options.nextPoint - 次のポイントオブジェクト。
 * @param {number} options.currentTimeStamp - 現在のタイムスタンプ。
 * @returns {number} 補間率。 0 ~ 1
 */
const calcInterpolateRate = ({
  prevPoint: pP, //Object
  nextPoint: nP, // Object
  currentTimeStamp: ct, // number
}) => {
  if (!isObject(pP)) return 0;
  if (!isObject(nP)) return 0;
  if (!isNumber(ct)) return 0;

  const _calcInterpolateRate = ({
    prevPointTimestamp: pPT,
    currentTimeStamp: ct,
    nextPointTimestamp: nPT,
  }) => {
    if (!isNumber(pPT) || !isNumber(ct) || !isNumber(nPT)) return 0;

    if (nPT === pPT) return 0; // 0除算回避
    return (ct - pPT) / (nPT - pPT);
  };

  return _calcInterpolateRate({
    prevPointTimestamp: getPointTimestamp(pP),
    currentTimeStamp: ct,
    nextPointTimestamp: getPointTimestamp(nP),
  });
};

/**
 * ポイントのタイムスタンプを取得します。
 * @param {Object} point - ポイントオブジェクト
 * @param {number} point.epoch - エポックタイムスタンプ
 * @param {number} point.t - タイムスタンプ
 * @returns {number} - タイムスタンプ
 */
const getPointTimestamp = (point) => {
  if (!isObject(point)) return 0;
  const { epoch, t } = point;
  if (!isNumber(epoch) && !isNumber(t)) return 0;

  return epoch || t;
};

/**
 * rgPlayBackObjのIDを取得します。
 * @param {Object} rgPlayBackObj
 * @returns {string} - Radar Grid再生オブジェクトのID
 * @throws {Error} - IDが無効な場合にエラーがスローされます
 */
const getRgPlaybackObjectId = (rgPlayBackObj) => {
  if (!hasRgPlaybackObjectValidId(rgPlayBackObj)) {
    throw new Error("Error Invalid Id");
  }
  const { vesselId, objectId } = rgPlayBackObj;
  return vesselId || objectId;
};

/**
 * rgPlayBackObjが有効なIDを持っているかどうかをチェックします。
 * @param {Object} rgPlayBackObj - チェックするrgPlayBackObj。
 * @returns {boolean} - rgPlayBackObjが有効なIDを持っている場合はtrue、それ以外の場合はfalseを返します。
 * idはvesselIdまたはobjectIdのいずれかです。
 */
const hasRgPlaybackObjectValidId = (rgPlayBackObj) => {
  if (!isObject(rgPlayBackObj)) return false;
  const { vesselId, objectId } = rgPlayBackObj;
  if (isString(vesselId) && vesselId.length > 0) return true;
  return isString(objectId) && objectId.length > 0;
};

/**
 * 補間率を考慮して、前のポイントと次のポイントから現在のポイントを計算します。
 *
 * @param {Object} prevPoint - 前のポイント
 * @param {number} interpolateRate - 補間率
 * @param {Object} nextPoint - 次のポイント
 * @returns {Object} 現在のポイント - 戻りの構造はprevPointと同じ
 * @throws {Error} 引数が無効な場合にエラーがスローされます
 */
const calcCurrentPoint = (prevPoint, nextPoint, interpolateRate = 0) => {
  if (!isObject(prevPoint)) throw new Error("Error Invalid Augments");
  if (!(isObject(nextPoint) || isUndefined(nextPoint))) {
    throw new Error("Error Invalid Augments");
  }
  if (!isNumber(interpolateRate)) throw new Error("Error Invalid Augments");
  if (!interpolateRate < 0 || 1 < interpolateRate) {
    throw new Error("Error Invalid Augments");
  }

  if (isUndefined(nextPoint)) return prevPoint;
  if (interpolateRate === 0) return prevPoint;

  const _calcInterpolateRate = (prevPoint, interpolateRate, nextPoint) => {
    return {
      ...prevPoint, // NOTE: lon, lat以外はprevPointの値を使用している
      lon: prevPoint.lon + (nextPoint.lon - prevPoint.lon) * interpolateRate,
      lat: prevPoint.lat + (nextPoint.lat - prevPoint.lat) * interpolateRate,
    };
  };
  return _calcInterpolateRate(prevPoint, interpolateRate, nextPoint);
};
